import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import { useLanguage } from "../../context/LanguageContext";
import { getLocalizedFieldValue } from "../../utils/language";
import he from "he";
import Marquee from "react-fast-marquee";
// partner slugify

const slugify = (text = "") =>
  text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")     // spaces → -
    .replace(/[^\w\u0600-\u06FF-]+/g, "") // allow Arabic
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildMediaUrl = (path = "") => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
  if (!base) return path;
  const normalizedBase = base.replace(/\/v1$/i, "");
  return `${normalizedBase}/${path.replace(/^\/+/, "")}`;
};

// Function to strip HTML tags and decode entities, then truncate
const truncateText = (text = "", maxLength = 120) => {
  if (!text) return "";
  
  // Decode HTML entities first
  const decoded = he.decode(text);
  
  // Strip HTML tags by creating a temporary element
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = decoded;
  const plainText = tempDiv.textContent || tempDiv.innerText || "";
  
  // Truncate if needed
  if (plainText.length <= maxLength) return plainText;
  return plainText.substring(0, maxLength).trim() + "...";
};

const normalizePartnerUpdates = (data) => {
  if (!data) return [];
  if (Array.isArray(data?.partnerUpdate)) return data.partnerUpdate;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
};

function PartnerUpdates() {
  const [activeType, setActiveType] = useState("all");
  const [updates, setUpdates] = useState([]);
  const [partnerTypes, setPartnerTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { language } = useLanguage();

  const normalizePartnerTypes = (data) => {
    if (!data) return [];
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchPartnerTypes = useCallback(async () => {
    try {
      const response = await handleApiCall("get", endpoints.partnerUpdateTypeList);
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true
      ) {
        const normalized = normalizePartnerTypes(response?.data?.data);
        setPartnerTypes(normalized);
      } else {
        console.error("Failed to fetch partner types:", response?.message);
        setPartnerTypes([]);
      }
    } catch (error) {
      console.error("Error fetching partner types:", error);
      setPartnerTypes([]);
    }
  }, []);

  useEffect(() => {
    fetchPartnerTypes();
  }, [fetchPartnerTypes]);

  const fetchPartnerUpdates = useCallback(async (typeId = "all") => {
    setLoading(true);
    try {
      const query = typeId && typeId !== "all" ? `?type=${typeId}` : "";
      const response = await handleApiCall(
        "get",
        `${endpoints.partnerUpdateList}${query}`
      );
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true
      ) {
        const normalized = normalizePartnerUpdates(response?.data?.data);
        setUpdates(normalized);
      } else {
        console.error("Failed to fetch partner updates:", response?.message);
        setUpdates([]);
      }
    } catch (error) {
      console.error("Error fetching partner updates:", error);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartnerUpdates(activeType);
  }, [fetchPartnerUpdates, activeType]);

  const slides = useMemo(() => {
    if (loading) {
      return [
        <SplideSlide key="loading" className="w-[350px] md:w-[450px] lg:w-[500px]">
          <div className="flex h-[300px] w-full items-center justify-center text-white text-lg bg-neutral-800 rounded-xl">
            {UI_CONSTANTS.BUTTON_LABELS.LOADING || "Loading..."}
          </div>
        </SplideSlide>,
      ];
    }

    if (!updates.length) {
      return null;
    }

    return updates.map((item, index) => {
      const title =
        getLocalizedFieldValue(item, language, "title") ||
        UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND;
      const fullDescription =
        getLocalizedFieldValue(item, language, "description") ||
        UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND;
      const description = truncateText(fullDescription, 200);
      // const imageUrl = buildMediaUrl(item?.image);
      const imageUrl = buildMediaUrl(item?.desktop_image);

      return (
        <SplideSlide
          key={item?._id || item?.id || index}
          className="w-[320px] md:w-[450px] lg:w-[500px]"
        >
          {/* <div className="relative h-[180px] md:h-[280px] lg:h-[300px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
            {imageUrl ? (
              <>
                <picture className="absolute inset-0 w-full h-full object-cover">
                    <source media="(min-width: 768px)" srcSet={item.desktop_image} />
                    <img
                      src={item.mobile_image}
                      alt={title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  </picture>
              </>
            ) : (
              <div className="absolute inset-0 bg-black/50" />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80" />
          </div> */}
          <div className="relative h-[180px] md:h-[280px] lg:h-[300px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
  {imageUrl ? (
    <picture className="absolute inset-0 w-full h-full object-cover">
      <source media="(min-width: 768px)" srcSet={item.desktop_image} />
      <img
        src={item.mobile_image}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
    </picture>
  ) : (
    <div className="absolute inset-0 bg-black/50" />
  )}

  {/* Dark green only behind title */}
  {/* <div className="absolute inset-x-0 bottom-0 h-2/3 
    bg-gradient-to-t 
    from-saudiaGreen/90 
    via-saudiaGreen/60 
    to-transparent" /> */}
    <div
  className="
  absolute inset-0
  bg-gradient-to-t
  from-saudiaGreen/65
  via-saudiaGreen/65
  to-saudiaGreen/65
" />
</div>

          <div className={`absolute caption px-3 z-10 ${language === "AR" ? "right-3 md:right-5" : "left-3 md:left-5"}`} dir={language === "AR" ? "rtl" : "ltr"}>
            <h3 className={`text-white text-lg font-SaudiaBold ${language === "AR" ? "md:text-xl" : "md:text-2xl"}`}>
              {title}
            </h3>
            <p className={`text-white mt-1 text-xs hidden md:text-sm font-SaudiaLight ${language === "AR" ? "pl-8 md:pl-8 lg:pl-20 leading-[18px] md:!leading-[26px]" : "pr-8 md:pr-8 lg:pr-20"}`}>
              {description}
            </p>
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // const partnerUpdateId = item?._id || item?.id;
                // if (partnerUpdateId) {
                //   const fullUrl = `${window.location.origin}/partner-update/${partnerUpdateId}`;
                //   window.open(fullUrl, "_blank", "noopener,noreferrer");
                // }

                // const slug = slugify(title);
                const slug =
                language === "AR"
                  ? slugify(item.title_ar || item.title_en)
                  : slugify(item.title_en || item.title_ar);
                console.log("slug", slug);
                if (slug) {
                  const fullUrl = `${window.location.origin}/partner-update/${slug}`;
                  window.open(fullUrl, "_blank", "noopener,noreferrer");
                }
              }}
              // className="mt-2 md:mt-4 md:px-5 md:py-2 bg-saudiaGreen text-white rounded-sm md:rounded-md w-[76px] md:w-[8.875rem] h-5 md:h-11 text-[10px] md:text-lg font-SaudiaLight md:font-SaudiaRegular cursor-pointer"
              className="
  mt-2 md:mt-4
  md:px-5 md:py-2
  bg-white
  text-saudiaGreen
  border border-white
  rounded-sm md:rounded-md
  w-[76px] md:w-[7.5rem]
  h-6 md:h-10
  text-[10px] md:text-sm
  font-SaudiaRegular
  leading-tight
  hover:bg-saudiaGreen
  hover:text-white
  transition
  cursor-pointer
"

            >
              {language === "AR" ? "اقرأ المزيد" : (UI_CONSTANTS.BUTTON_LABELS.READ_MORE || "Read More")}
            </button>
          </div>
        </SplideSlide>
      );
    });
  }, [updates, loading, language]);

  const renderAutoScrollSlider = (speed = 1) => (
    <Splide
      options={{
        type: "loop",
        rewind: true,
        gap: "18px",
        perPage: 2,
        autoWidth: true,
        arrows: false,
        pagination: false,
        perMove: 1,
        drag: false,
        autoScroll: {
          speed,
          pauseOnHover: true,
          pauseOnFocus: false,
        },
        direction: language === "AR" ? "rtl" : "ltr",
      }}
      extensions={{ AutoScroll }}
    >
      {slides}
    </Splide>
  );
  const [isSmallScreen, setIsSmallScreen] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1070);

    handleResize(); // initial
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isOdd = slides && slides.length % 2 !== 0;
  return (
    <section className='bg-white overflow-hidden [&_.splide\_\_track]:!overflow-visible py-10 md:py-20 [&_.oddNumber]:gap-7'>
      <div className='px-6 md:px-8 lg:px-8 2xl:px-0 max-w-8xl mx-auto flex flex-col'>
        <div className={`md:flex items-center mb-10 gap-x-8 lg:gap-x-16 ${language === "AR" ? "xl:ml-20 2xl:mr-28" : "xl:ml-12 2xl:ml-28"}`}>
          <h2 className={`text-saudiaGreen md:text-[2rem] 2xl:text-[2.625rem] ${language === "AR" ? "text-[22px] font-SaudiaBold lg:text-[1.85rem]":"text-[28px] font-SaudiaBoldItalic lg:text-[2rem]"}`}>
            {/* Partner Updates */}
            {language === "AR" ? "اخر اخبار شراكاتنا" : "Partner Updates"}
          </h2>
          <div className={`d-flex mt-4 md:mt-0 [&_bg-saudiaGreen] text-[8px] md:text-sm flex gap-x-2 md:gap-x-3 [&_button]:border [&_button]:border-saudiaGreen [&_button]:min-h-[18px] md:[&_button]:p-2.5 [&_button]:rounded-[4px] md:[&_button]:rounded-lg [&_button]:min-w-[78px] [&_button]:whitespace-nowrap lg:[&_button]:min-w-[150px] ${language === "AR" ? "" : ""}`}>
            {/* "All" tab - always first */}
            <button
              key="all"
              onClick={() => setActiveType("all")}
              className={`${
                activeType === "all"
                  ? "bg-saudiaGreen text-white md:font-SaudiaBoldd"
                  : "text-saudiaGreen text-wrap"
              }`}
            >
              {language === "AR" ? "الكل" : "All"}
            </button>
            {/* Dynamic partner type tabs */}
            {partnerTypes.map((type) => {
              // const label = getLocalizedFieldValue(type, language, "title") || type.title_en || "";
               const label = language === "AR" 
                ? type.title_ar 
                : type.title_en;                
              return (
                <button
                  key={type._id || type.id}
                  onClick={() => setActiveType(type._id || type.id)}
                  className={`${
                    activeType === (type._id || type.id)
                      ? "bg-saudiaGreen text-white md:font-SaudiaBoldd"
                      : "text-saudiaGreen"
                  } text-wrap`}

                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        {/* <div>
          {slides ? (
            <div className="skewSlider flex flex-col gap-y-6">
              {renderAutoScrollSlider(-1)}
            </div>
          ) : (
            <div className="text-saudiaGreen ml-28 text-lg font-SaudiaRegular">
              {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
            </div>
          )}
        </div> */}
      </div>
      {slides && slides.length > 2 ? (
      <Marquee
        speed={70}
        pauseOnHover={true}
        direction={language === "AR" ? "left" : "right"}
        className={`font-SaudiaExtraBold text-3xl md:text-[30px] lg:text-[42px] text-white flex items-center flex-row pt-[6px] w-full !overflow-visible skewSlider [&_.rfm-child]:mx-2.5 ltr ${
          isOdd ? "oddNumber" : ""
        }`}
        dir="ltr"
      >
        {slides}
      </Marquee>
    ) : slides ? (
      <>
        {isSmallScreen ? (
          <Marquee
            speed={70}
            pauseOnHover={true}
            direction={language === "AR" ? "left" : "right"}
            className={`w-full !overflow-visible skewSlider [&_.rfm-child]:mx-2.5 ltr ${
              isOdd ? "oddNumber" : ""
            }`}
            dir="ltr"
          >
            <div className="flex gap-5 px-6 md:px-8 lg:px-8 2xl:px-0 max-w-8xl mx-auto">
              {slides}
            </div>
          </Marquee>
        ) : (
          <div
            className={`flex gap-5 skewSlider px-6 md:px-8 lg:px-8 2xl:px-0 max-w-8xl mx-auto ${
              isOdd ? "oddNumber" : ""
            }`}
          >
            {slides}
          </div>
        )}
      </>
    ) : (
      <div className="text-saudiaGreen ml-28 text-lg font-SaudiaRegular">
        {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
      </div>
    )}
    </section>
  );
}

export default PartnerUpdates;

